import React from 'react'
import { graphql } from 'gatsby'
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews'

// Components
import Breadcrumb from '../components/breadcrumb'
import Layout from '../components/layout'
import SearchForm from '../components/searchForm'
import Seo from '../components/seo'
import SliceList from '../components/sliceList'
import Video from '../components/video'

import KeyOn from '../components/svg/keyOn'
import EVStation from '../components/svg/evStation'
import FlightTakeOff from '../components/svg/flightTakeOff'
import MapPin from '../components/svg/mapPin'
import Clock from '../components/svg/clock'
import Walk from '../components/svg/walk'
import Handshake from '../components/svg/handshake'

export const query = graphql`
  query CarPark($id: String, $productCode: String) {
    prismicCarPark(data: { hx_product_code: { eq: $productCode } }) {
      ...PrismicCarParkFragment
    }
    holidayExtrasParking(id: { eq: $id }) {
      ...HolidayExtrasParkingFragment
    }
  }
`

const omitNull = (obj) => {
  if (!obj) return {}
  Object.keys(obj)
    .filter((k) => obj[k] === null)
    .forEach((k) => delete obj[k])
  return obj
}

const CarParkTemplate = ({ data, location }) => {
  let prismicPage
  if (data?.prismicCarPark?.data) {
    prismicPage = Object.fromEntries(Object.entries(data?.prismicCarPark?.data).filter(([_, v]) => v != null))
  }

  const pageData = {
    ...omitNull(data.holidayExtrasParking),
    ...omitNull(prismicPage?.holiday_extras_fields[0])
  }

  // Do Not Remove
  console.log('%cHX PRODUCT CODE', 'color:red', pageData.productCode)

  const pros = prismicPage?.pros
  const cons = prismicPage?.cons
  const faqs = prismicPage?.faqs

  const summaryClass =
    'flex cursor-pointer rounded-md p-4 text-orange-500 outline-none ring-inset hover:text-orange-600 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 text-xl leading-snug tracking-tight font-medium'
  return (
    <Layout>
      <div>
        <div className='w-full py-3 md:py-6'>
          <SearchForm product='Parking' location={pageData.airport} />
        </div>
        <div className='mx-auto max-w-screen-xl px-4'>
          <Breadcrumb location={location} />
        </div>
        <div className='mx-auto flex max-w-screen-xl flex-row items-start gap-6 px-4 py-10'>
          {pageData.logo && <img src={pageData.logo.url} className='w-32 rounded-sm bg-white object-cover p-1 shadow-md' alt={`${pageData.name} logo` || ''} />}
          <div className='flex flex-1 flex-col'>
            <h1 className='mb-2 font-serif text-3xl leading-snug tracking-tight text-orange-500 md:text-4xl lg:text-5xl'>
              {pageData.page_title ? pageData.page_title : pageData.name}
              {pageData.airport && ` - ${pageData.airport}`}
            </h1>
            <div className='prose prose-lg min-w-full leading-snug md:prose-xl'>{pageData.sellpoint}</div>
          </div>
        </div>
      </div>
      <div className='relative bg-blue-500 py-10 text-white'>
        <div className='mx-auto grid max-w-screen-xl grid-cols-2 gap-x-6 gap-y-12 px-4 md:grid-cols-4 md:gap-x-8 md:gap-y-16'>
          {pageData.on_airport && !pageData.meet_and_greet && (
            <div className='flex justify-start gap-4 font-serif text-lg md:block md:text-2xl'>
              <div className='h-12 w-12 pb-1'>
                <MapPin />
              </div>
              <div>
                <div className='text-sm uppercase text-blue-200'>Location</div>
                On airport
              </div>
            </div>
          )}
          {!pageData.on_airport && !pageData.meet_and_greet && (
            <div className='flex justify-start gap-4 font-serif text-lg md:block md:text-2xl'>
              <div className='h-12 w-12 pb-1'>{pageData.transfer_frequency === 0 || pageData.transfer_frequency === '0' ? <Walk /> : <Clock />}</div>
              <div>
                <div className='text-sm uppercase text-blue-200'>Transfers</div>
                {pageData.transfer_time && <div>{pageData.transfer_time} mins</div>}
                {pageData.transfer_frequency !== 0 && pageData.transfer_frequency !== '0' && <div className='text-base'>(every {pageData.transfer_frequency} mins)</div>}
              </div>
            </div>
          )}
          {pageData.meet_and_greet && (
            <div className='flex justify-start gap-4 font-serif text-lg md:block md:text-2xl'>
              <div className='h-12 w-12 pb-1'>
                <Handshake />
              </div>
              <div>
                <div className='text-sm uppercase text-blue-200'>Service</div>
                Meet and Greet
              </div>
            </div>
          )}
          <div className='flex justify-start gap-4 font-serif text-lg md:block md:text-2xl'>
            <div className='h-12 w-12 pb-1'>
              <FlightTakeOff />
            </div>
            <div>
              <div className='text-sm uppercase text-blue-200'>Terminal served</div>
              {pageData.terminal ? pageData.terminal : 'All'}
            </div>
          </div>
          <div className='flex justify-start gap-4 font-serif text-lg md:block md:text-2xl'>
            <div className='h-12 w-12 pb-1 '>
              <EVStation />
            </div>
            <div>
              <div className='text-sm uppercase text-blue-200'>Electric Charging</div>
              {pageData.has_electric_charging === true ? 'Available' : 'Not Available'}
            </div>
          </div>
          <div className='flex gap-4 font-serif text-lg md:block md:text-2xl'>
            <div className='h-12 w-12 pb-1'>
              <KeyOn />
            </div>
            <div>
              <div className='text-sm uppercase text-blue-200'>Keep your Keys</div>
              {pageData.keep_keys === true ? 'Yes' : 'No, your car is parked for you'}
            </div>
          </div>
        </div>
      </div>
      {(pros?.[0]?.text || cons?.[0]?.text) && (
        <section className='prose mx-auto grid max-w-screen-xl grid-cols-2 py-6'>
          <div className='p-4'>
            <h2>Pros</h2>
            <ul>
              {pros?.map((pro, index) => {
                if (!pro.text) return null
                return <li key={`pro-${index}`}>{pro.text}</li>
              })}
            </ul>
          </div>
          <div className='p-4'>
            <h2>Cons</h2>
            <ul>
              {cons?.map((con, index) => {
                if (!con.text) return null
                return <li key={`con-${index}`}>{con.text}</li>
              })}
            </ul>
          </div>
        </section>
      )}
      {pageData.videoid && <Video id={pageData.videoid} />}
      {(pageData.arrival_procedures || pageData.departure_procedures) && (
        <section className='prose relative mx-auto max-w-screen-xl border-b px-4 py-6'>
          <h2>What to expect</h2>
          <h3>When you arrive at {pageData.name}</h3>
          {pageData.arrival_procedures && <div dangerouslySetInnerHTML={{ __html: pageData.arrival_procedures }} />}
          <h3>On your return to {pageData.airport && <span dangerouslySetInnerHTML={{ __html: pageData.airport }} />}</h3>
          {pageData.departure_procedures && <div dangerouslySetInnerHTML={{ __html: pageData.departure_procedures }} />}
        </section>
      )}
      {pageData.information && (
        <section className='prose relative mx-auto max-w-screen-xl border-b px-4 py-6'>
          <h2>Important Information</h2>
          <div dangerouslySetInnerHTML={{ __html: pageData.information }} />
        </section>
      )}
      {(pageData.disabled_facilities || pageData.covid_copy || pageData.maximum_car_size || pageData.security_measures || faqs?.[0]?.question) && (
        <section itemScope itemType='https://schema.org/FAQPage' className='prose relative mx-auto max-w-screen-xl border-b px-4 py-6'>
          <h2>Frequently Asked Questions </h2>
          <div className='not-prose'>
            <ul className='mt-5'>
              {faqs?.map((faq, index) => {
                if (!faq.question || !faq.answer) return null
                return (
                  <li key={`faq-${index}`} itemScope itemProp='mainEntity' itemType='https://schema.org/Question'>
                    <details className='mb-2 overflow-hidden rounded-md bg-white shadow-sm md:mb-3'>
                      <summary className={summaryClass}>
                        <div className='m-0 flex-1' itemProp='name'>
                          {faq.question}
                        </div>
                        <div className='indicator float-right flex'>
                          <svg xmlns='http://www.w3.org/2000/svg' height='30' width='30' viewBox='0 0 24 24' fill='currentColor'>
                            <path d='M0 0h24v24H0V0z' fill='none'></path>
                            <path d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z'></path>
                          </svg>
                        </div>
                      </summary>
                      <div className='p-4 pt-0' itemScope itemProp='acceptedAnswer' itemType='https://schema.org/Answer'>
                        <div itemProp='text' className='prose prose-blue min-w-full'>
                          {faq.question}
                        </div>
                      </div>
                    </details>
                  </li>
                )
              })}
              {pageData.disabled_facilities && (
                <li itemScope itemProp='mainEntity' itemType='https://schema.org/Question'>
                  <details className='mb-2 overflow-hidden rounded-md bg-white shadow-sm md:mb-3'>
                    <summary className={summaryClass}>
                      <div className='m-0 flex-1' itemProp='name'>
                        What about any accessibility requirements?
                      </div>
                      <div className='indicator float-right flex'>
                        <svg xmlns='http://www.w3.org/2000/svg' height='30' width='30' viewBox='0 0 24 24' fill='currentColor'>
                          <path d='M0 0h24v24H0V0z' fill='none'></path>
                          <path d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z'></path>
                        </svg>
                      </div>
                    </summary>
                    <div className='p-4 pt-0' itemScope itemProp='acceptedAnswer' itemType='https://schema.org/Answer'>
                      <div itemProp='text' className='prose prose-blue min-w-full' dangerouslySetInnerHTML={{ __html: pageData.disabled_facilities }} />
                    </div>
                  </details>
                </li>
              )}
              {pageData.covid_copy && (
                <li itemScope itemProp='mainEntity' itemType='https://schema.org/Question'>
                  <details className='mb-2 overflow-hidden rounded-md bg-white shadow-sm md:mb-3'>
                    <summary className={summaryClass}>
                      <div className='m-0 flex-1' itemProp='name'>
                        What is the COVID policy?
                      </div>
                      <div className='indicator float-right flex'>
                        <svg xmlns='http://www.w3.org/2000/svg' height='30' width='30' viewBox='0 0 24 24' fill='currentColor'>
                          <path d='M0 0h24v24H0V0z' fill='none'></path>
                          <path d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z'></path>
                        </svg>
                      </div>
                    </summary>
                    <div className='p-4 pt-0' itemScope itemProp='acceptedAnswer' itemType='https://schema.org/Answer'>
                      <div itemProp='text' className='prose prose-blue min-w-full' dangerouslySetInnerHTML={{ __html: pageData.covid_copy }} />
                    </div>
                  </details>
                </li>
              )}
              {pageData.maximum_car_size && (
                <li itemScope itemProp='mainEntity' itemType='https://schema.org/Question'>
                  <details className='mb-2 overflow-hidden rounded-md bg-white shadow-sm md:mb-3'>
                    <summary className={summaryClass}>
                      <div className='m-0 flex-1' itemProp='name'>
                        Is there a maximum car size?
                      </div>
                      <div className='indicator float-right flex'>
                        <svg xmlns='http://www.w3.org/2000/svg' height='30' width='30' viewBox='0 0 24 24' fill='currentColor'>
                          <path d='M0 0h24v24H0V0z' fill='none'></path>
                          <path d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z'></path>
                        </svg>
                      </div>
                    </summary>
                    <div className='p-4 pt-0' itemScope itemProp='acceptedAnswer' itemType='https://schema.org/Answer'>
                      <div itemProp='text' className='prose prose-blue min-w-full' dangerouslySetInnerHTML={{ __html: pageData.maximum_car_size }} />
                    </div>
                  </details>
                </li>
              )}
              {pageData.security_measures && (
                <li itemScope itemProp='mainEntity' itemType='https://schema.org/Question'>
                  <details className='mb-2 overflow-hidden rounded-md bg-white shadow-sm md:mb-3'>
                    <summary className={summaryClass}>
                      <div className='m-0 flex-1' itemProp='name'>
                        What security measures are in place?
                      </div>
                      <div className='indicator float-right flex'>
                        <svg xmlns='http://www.w3.org/2000/svg' height='30' width='30' viewBox='0 0 24 24' fill='currentColor'>
                          <path d='M0 0h24v24H0V0z' fill='none'></path>
                          <path d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z'></path>
                        </svg>
                      </div>
                    </summary>
                    <div className='p-4 pt-0' itemScope itemProp='acceptedAnswer' itemType='https://schema.org/Answer'>
                      <div itemProp='text' className='prose prose-blue min-w-full' dangerouslySetInnerHTML={{ __html: pageData.security_measures }} />
                    </div>
                  </details>
                </li>
              )}
            </ul>
          </div>
        </section>
      )}
      {pageData.address && (
        <section className='prose relative mx-auto max-w-screen-xl border-b px-4 py-6'>
          <h2>Location</h2>
          <div className='gap-4 md:grid md:grid-cols-3'>
            <div>
              <h3>Address</h3>
              {pageData.address && <div dangerouslySetInnerHTML={{ __html: pageData.address }} />}
              {pageData.telephone && (
                <>
                  <h4>Contact Number</h4>
                  <p>{pageData.telephone}</p>
                </>
              )}
            </div>
            <div className='md:col-span-2'>
              <div className='relative aspect-video '>
                <iframe
                  className='absolute inset-0 rounded bg-white p-2 shadow'
                  width='100%'
                  height='100%'
                  title='Google Map of Venue'
                  src={`https://www.google.com/maps/embed/v1/place?q=${pageData.latitude},${pageData.longitude}&key=${process.env.GATSBY_GOOGLE_API_KEY}`}
                  allowFullScreen
                />
              </div>
              <div className='mt-2 space-x-2 text-right text-sm'>
                {pageData.latitude && (
                  <span>
                    <strong>Latitude</strong> {pageData.latitude}
                  </span>
                )}
                {pageData.longitude && (
                  <span>
                    <strong>Longitude</strong> {pageData.longitude}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className='col-span-2'>
            {pageData.directions && (
              <>
                <h4>Directions</h4>
                <div dangerouslySetInnerHTML={{ __html: pageData.directions }} />
              </>
            )}
          </div>
        </section>
      )}
      <SliceList slices={pageData.body} />
    </Layout>
  )
}

export default withPrismicPreview(CarParkTemplate)

export const Head = ({ data, location }) => {
  let prismicPage
  if (data?.prismicCarPark?.data) {
    prismicPage = Object.fromEntries(Object.entries(data?.prismicCarPark?.data).filter(([_, v]) => v != null))
  }

  const pageData = {
    ...omitNull(data.holidayExtrasParking),
    ...omitNull(prismicPage?.holiday_extras_fields[0])
  }
  return (
    <>
      <html lang={data.prismicCarPark?.lang || 'en-gb'} />
      <Seo
        pathname={location.pathname}
        title={pageData.meta_title || `${pageData.name} - ${pageData.airport}`}
        description={pageData.meta_description || `Information about ${pageData.name} - ${pageData.sellpoint}`}
        image={pageData.page_image?.url || pageData.logo?.url}
        pros={prismicPage?.pros}
        cons={prismicPage?.cons}
      />
    </>
  )
}
